import { Capacitor } from "@capacitor/core";
import { TextToSpeech } from "@capacitor-community/text-to-speech";
import { supabase } from "@/integrations/supabase/client";
import { getTTSVoiceIndex } from "@/lib/ttsVoice";
import {
  beginCoachBackgroundTask,
  endCoachBackgroundTask,
  playDuckedNativeAudio,
} from "@/lib/straivNowPlaying";
import { sanitizeCoachSpeechText } from "@/lib/runningCoach";

/**
 * Cloud TTS (OpenAI/ElevenLabs via coach-voice-tts) for AI coach / in-run guidance.
 * speakCoachCue prefers cloud TTS and falls back to on-device voice if cloud fails.
 * speakNativeNow is for countdown, normal-run stats, pause/resume/finish only.
 */

export type SpeakOptions = {
  voice?: "male" | "female";
  /** Default true — OpenAI/ElevenLabs only. Set false only for emergency offline. */
  useNatural?: boolean;
  /** When true, clear the queue and speak immediately. */
  interrupt?: boolean;
  /** Jump ahead of queued cues (e.g. "run started" before coach intro). */
  priority?: boolean;
  /** When true (default for speakCoachCue), use on-device voice if cloud TTS fails. */
  nativeFallback?: boolean;
  /**
   * Resolve spoken text immediately before the cloud TTS fetch (after any queue wait).
   * Use for live elapsed/pace/HR so numbers match watch-now, not draft-time.
   */
  resolveText?: () => string;
  /** Fewer cloud retries / slightly tighter timeout for mid-run live cues. */
  live?: boolean;
};

const NATURAL_TIMEOUT_MS = 12000;
const COACH_TTS_ATTEMPTS = 5;
const COACH_TTS_LIVE_ATTEMPTS = 2;

let currentAudio: HTMLAudioElement | null = null;
let cachedAvailable: boolean | null = null;

/** Locked TTS profile for the current coach run — prevents ElevenLabs/OpenAI switching mid-run. */
let lockedVoiceProfile: { provider: string; voiceId?: string } | null = null;

type NaturalFetchResult = { mime: string; b64: string; provider?: string; voiceId?: string };

type QueueItem = {
  text: string;
  options?: SpeakOptions;
  resolve: () => void;
  priority?: boolean;
};

const speechQueue: QueueItem[] = [];
let processingQueue = false;
/** Bumped on interrupt/stop so in-flight TTS fetch does not play after cancel. */
let playbackGeneration = 0;

const stopAudioElement = () => {
  try {
    if (currentAudio) {
      currentAudio.onended = null;
      currentAudio.onerror = null;
      currentAudio.pause();
      currentAudio.src = "";
      currentAudio = null;
    }
  } catch {}
};

const stopNativeTts = () => {
  try {
    if (Capacitor.isNativePlatform()) {
      void TextToSpeech.stop().catch(() => undefined);
    } else if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
  } catch {}
};

const stopAllPlayback = () => {
  playbackGeneration += 1;
  stopAudioElement();
  stopNativeTts();
};

const base64ToBlob = (b64: string, mime: string): Blob => {
  const byteChars = atob(b64);
  const len = byteChars.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = byteChars.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

async function speakNative(text: string, voice: "male" | "female"): Promise<void> {
  stopNativeTts();
  if (Capacitor.isNativePlatform()) {
    // Do NOT touch AVAudioSession here. The patched TTS plugin sets
    // usesApplicationAudioSession = false so iOS ducks music on a private
    // voice-prompt session (Maps-style). beginAudioDucking() steals that
    // session and pauses Spotify/Apple Music instead of ducking.
    const voiceIndex = await getTTSVoiceIndex(voice);
    const opts: Record<string, unknown> = {
      text,
      lang: "en-US",
      rate: 0.92,
      pitch: voice === "male" ? 0.95 : 1.02,
      volume: 1.0,
      queueStrategy: 1,
      duckOthers: false,
      ...(voiceIndex !== undefined && voiceIndex >= 0 && { voice: voiceIndex }),
    };
    await TextToSpeech.speak(opts as unknown as Parameters<typeof TextToSpeech.speak>[0]).catch(() => undefined);
    return;
  }

  if (typeof window !== "undefined" && "speechSynthesis" in window) {
    await new Promise<void>((resolve) => {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.92;
      utterance.pitch = voice === "male" ? 0.95 : 1.05;
      utterance.volume = 1.0;
      utterance.onend = () => resolve();
      utterance.onerror = () => resolve();
      window.speechSynthesis.speak(utterance);
    });
  }
}

async function fetchNatural(
  text: string,
  voice: "male" | "female",
  opts?: { ignoreCache?: boolean },
): Promise<NaturalFetchResult | null> {
  if (!opts?.ignoreCache && cachedAvailable === false) return null;
  try {
    const body: Record<string, unknown> = { text, voice };
    if (lockedVoiceProfile) {
      body.lockProvider = lockedVoiceProfile.provider;
      if (lockedVoiceProfile.voiceId) body.lockVoiceId = lockedVoiceProfile.voiceId;
    }
    const { data, error } = await supabase.functions.invoke("coach-voice-tts", {
      body,
    });
    const d = data as {
      provider?: string;
      voice_id?: string;
      mime?: string;
      audio_base64?: string;
      reason?: string;
    };
    if (error || !d?.audio_base64 || !d?.mime) {
      console.warn("[NaturalVoice] cloud TTS unavailable:", error ?? d?.reason ?? "no audio");
      if (d?.reason === "no_tts_provider_configured") cachedAvailable = false;
      return null;
    }
    cachedAvailable = true;
    if (d.provider && !lockedVoiceProfile) {
      lockedVoiceProfile = { provider: d.provider, voiceId: d.voice_id };
      console.log("[NaturalVoice] Locked coach voice:", lockedVoiceProfile);
    }
    return { mime: d.mime, b64: d.audio_base64, provider: d.provider, voiceId: d.voice_id };
  } catch (e) {
    console.warn("[NaturalVoice] fetch failed:", e);
    return null;
  }
}

/** Cloud TTS with retries — used for all coach / AI guide speech. */
async function fetchNaturalWithRetry(
  text: string,
  voice: "male" | "female",
  attempts = COACH_TTS_ATTEMPTS,
): Promise<{ mime: string; b64: string } | null> {
  for (let i = 0; i < attempts; i++) {
    const result = await fetchNatural(text, voice, { ignoreCache: i > 0 });
    if (result) return result;
    if (i < attempts - 1) {
      await new Promise((r) => setTimeout(r, 600 * (i + 1)));
    }
  }
  return null;
}

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const b64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(b64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function playBlob(blob: Blob): Promise<void> {
  const useNativeIos =
    Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";

  if (useNativeIos) {
    return blobToBase64(blob).then(async (b64) => {
      await playDuckedNativeAudio(b64, blob.type || "audio/mpeg");
    });
  }

  return new Promise((resolve) => {
    try {
      stopAudioElement();
      const audio = new Audio();
      currentAudio = audio;
      audio.preload = "auto";
      audio.src = URL.createObjectURL(blob);
      const cleanup = () => {
        try {
          if (audio.src) URL.revokeObjectURL(audio.src);
        } catch {}
        if (currentAudio === audio) currentAudio = null;
        resolve();
      };
      audio.onended = cleanup;
      audio.onerror = cleanup;
      void audio.play().catch((err) => {
        console.warn("[NaturalVoice] audio.play failed:", err);
        cleanup();
      });
    } catch (e) {
      console.warn("[NaturalVoice] playBlob threw:", e);
      resolve();
    }
  });
}

async function speakOne(text: string, options?: SpeakOptions): Promise<void> {
  const gen = playbackGeneration;
  let clean = sanitizeCoachSpeechText(String(text || "").trim());
  if (options?.resolveText) {
    try {
      clean = sanitizeCoachSpeechText(String(options.resolveText() || "").trim());
    } catch (e) {
      console.warn("[NaturalVoice] resolveText failed:", e);
      clean = "";
    }
  }
  if (!clean) return;
  const voice = options?.voice === "male" ? "male" : "female";
  const useCloud = options?.useNatural !== false;
  const allowNativeFallback = options?.nativeFallback !== false;
  const useNativeIos =
    Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
  const attempts = options?.live ? COACH_TTS_LIVE_ATTEMPTS : COACH_TTS_ATTEMPTS;

  if (useNativeIos) {
    beginCoachBackgroundTask();
  }

  try {
    if (useCloud) {
      stopNativeTts();
      // Resolve again immediately before fetch so live metrics age as little as possible.
      if (options?.resolveText) {
        try {
          const again = sanitizeCoachSpeechText(String(options.resolveText() || "").trim());
          if (again) clean = again;
        } catch {}
      }
      if (gen !== playbackGeneration) return;
      const natural = await fetchNaturalWithRetry(clean, voice, attempts);
      if (gen !== playbackGeneration) return;
      if (natural) {
        const blob = base64ToBlob(natural.b64, natural.mime);
        await playBlob(blob);
        return;
      }
      if (gen !== playbackGeneration) return;
      if (allowNativeFallback) {
        console.warn("[NaturalVoice] cloud TTS failed, using on-device voice:", clean.slice(0, 40));
        await speakNative(clean, voice);
        return;
      }
      console.warn("[NaturalVoice] cloud TTS failed, skipping cue (no robot fallback):", clean.slice(0, 40));
      return;
    }

    if (gen !== playbackGeneration) return;
    await speakNative(clean, voice);
  } finally {
    if (useNativeIos) {
      endCoachBackgroundTask();
    }
  }
}

async function drainQueue(): Promise<void> {
  if (processingQueue) return;
  processingQueue = true;
  try {
    while (speechQueue.length > 0) {
      const item = speechQueue.shift()!;
      try {
        await speakOne(item.text, item.options);
      } catch (e) {
        console.warn("[NaturalVoice] speakOne failed:", e);
      }
      item.resolve();
    }
  } finally {
    processingQueue = false;
  }
}

/** Speak via cloud TTS (queued). Returns when playback finishes. */
export function speakCoachCue(text: string, options?: SpeakOptions): Promise<void> {
  const hasResolver = typeof options?.resolveText === "function";
  const clean = hasResolver ? String(text || "").trim() : sanitizeCoachSpeechText(String(text || "").trim());
  if (!clean && !hasResolver) return Promise.resolve();

  if (options?.interrupt) {
    const dropped = speechQueue.splice(0, speechQueue.length);
    for (const item of dropped) {
      try {
        item.resolve();
      } catch {}
    }
    stopAllPlayback();
  }

  return new Promise<void>((resolve) => {
    const item: QueueItem = {
      text: clean,
      options: { ...options, nativeFallback: true, useNatural: true },
      resolve,
      priority: options?.priority === true,
    };
    if (item.priority) speechQueue.unshift(item);
    else speechQueue.push(item);
    void drainQueue();
  });
}

export function stopCoachVoice(): void {
  const dropped = speechQueue.splice(0, speechQueue.length);
  for (const item of dropped) {
    try {
      item.resolve();
    } catch {}
  }
  stopAllPlayback();
  // Do NOT endAudioDucking here — that ran before every cloud clip and churned
  // the session. Native playDuckedAudio owns duck start; finish callback ends it.
}

export function resetCoachVoiceSession(): void {
  cachedAvailable = null;
  lockedVoiceProfile = null;
}

/** @deprecated use resetCoachVoiceSession */
export function resetNaturalVoiceCache(): void {
  resetCoachVoiceSession();
}

/**
 * Pre-fetch a TTS clip as a Blob without queuing/playback. Useful for warming
 * up the cloud TTS function and pre-buffering audio (e.g. for countdown so
 * playback is instant once the visual shows the digit).
 */
export async function prefetchCoachAudio(
  text: string,
  voice: "male" | "female" = "female",
): Promise<Blob | null> {
  const clean = String(text || "").trim();
  if (!clean) return null;
  const natural = await fetchNaturalWithRetry(clean, voice);
  if (!natural) return null;
  return base64ToBlob(natural.b64, natural.mime);
}

/**
 * Play a pre-fetched Blob immediately (no queue, no cloud round-trip).
 * Resolves when the audio finishes.
 */
export function playCoachAudio(blob: Blob | null): Promise<void> {
  if (!blob) return Promise.resolve();
  stopCoachVoice();
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios") {
    beginCoachBackgroundTask();
    return playBlob(blob).finally(() => {
      endCoachBackgroundTask();
    });
  }
  return playBlob(blob);
}

/**
 * Speak via on-device voice immediately (no queue, no cloud).
 * Always resolves when speech finishes or fails.
 */
export function speakNativeNow(
  text: string,
  voice: "male" | "female" = "female",
): Promise<void> {
  const clean = String(text || "").trim();
  if (!clean) return Promise.resolve();
  return speakNative(clean, voice);
}
